import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Markets.css';

const Indices: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const majorIndices = [
    { name: 'S&P 500', region: 'USA', description: 'Top 500 US companies by market cap' },
    { name: 'Dow Jones 30', region: 'USA', description: '30 large publicly-owned US companies' },
    { name: 'NASDAQ 100', region: 'USA', description: 'Top 100 non-financial companies on NASDAQ' },
    { name: 'FTSE 100', region: 'UK', description: '100 companies with highest market cap on LSE' },
    { name: 'DAX 40', region: 'Germany', description: '40 major German companies on Frankfurt Stock Exchange' },
    { name: 'CAC 40', region: 'France', description: '40 most significant stocks on Euronext Paris' },
    { name: 'Nikkei 225', region: 'Japan', description: '225 top-rated companies on Tokyo Stock Exchange' },
    { name: 'Hang Seng', region: 'Hong Kong', description: 'Largest companies on Hong Kong Stock Exchange' },
    { name: 'ASX 200', region: 'Australia', description: '200 largest companies by market cap in Australia' }
  ];

  return (
    <motion.div 
      className="markets-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Stock Indices Trading</h1>
          <p>Trade global stock market indices with professional analysis</p>
        </motion.div>

        

        <motion.section className="market-overview" variants={itemVariants}>
          <h2>What are Stock Indices?</h2>
          <p>
            A stock index is a measurement of the value of a section of the stock market. It is computed from the prices of selected stocks 
            and serves as an indicator of the overall health and direction of the market or specific sector it represents.
          </p>
          <p>
            Trading indices allows you to gain exposure to an entire economy or sector with a single trade, rather than selecting individual stocks. 
            This provides diversification and often lower volatility compared to trading individual equities.
          </p>
        </motion.section>

        <motion.section className="market-features" variants={itemVariants}>
          <h2>Why Trade Indices?</h2>
          <div className="features-grid">
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Diversification</h3>
              <p>Gain exposure to multiple companies and sectors with a single position</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Lower Volatility</h3>
              <p>Indices are generally less volatile than individual stocks</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Extended Trading Hours</h3>
              <p>Trade major indices nearly 24 hours a day with CFDs</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Market Sentiment</h3>
              <p>Indices reflect overall market sentiment and economic health</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Leverage Opportunities</h3>
              <p>Access larger positions with leverage trading (use with caution)</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>No Single Company Risk</h3>
              <p>Reduce exposure to individual company failures or scandals</p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="indices-list" variants={itemVariants}>
          <h2>Major Global Indices</h2>
          <div className="indices-grid">
            {majorIndices.map((index, i) => (
              <motion.div key={i} className="index-card" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <h3>{index.name}</h3>
                <p className="region"><strong>Region:</strong> {index.region}</p>
                <p>{index.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Popular Index Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📈 Trend Following</h3>
              <p>Identify and trade in the direction of the prevailing market trend</p>
            </div>
            <div className="strategy">
              <h3>📊 Range Trading</h3>
              <p>Trade between support and resistance levels in sideways markets</p>
            </div>
            <div className="strategy">
              <h3>💥 Breakout Trading</h3>
              <p>Enter positions when price breaks through key support or resistance levels</p>
            </div>
            <div className="strategy">
              <h3>📰 News Trading</h3>
              <p>Capitalize on market movements caused by economic data releases</p>
            </div>
            <div className="strategy">
              <h3>🔄 Mean Reversion</h3>
              <p>Trade based on the assumption that prices will return to their average</p>
            </div>
            <div className="strategy">
              <h3>⏰ Session Trading</h3>
              <p>Focus on specific trading sessions when volatility is highest</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>Factors Affecting Index Prices</h2>
          <div className="factors-list">
            <div className="factor">
              <h4>📈 Corporate Earnings</h4>
              <p>Quarterly earnings reports from major constituent companies</p>
            </div>
            <div className="factor">
              <h4>🏦 Central Bank Policy</h4>
              <p>Interest rate decisions and monetary policy announcements</p>
            </div>
            <div className="factor">
              <h4>💹 Economic Data</h4>
              <p>GDP, employment, inflation, and manufacturing data releases</p>
            </div>
            <div className="factor">
              <h4>🌍 Geopolitical Events</h4>
              <p>Elections, trade agreements, and international conflicts</p>
            </div>
            <div className="factor">
              <h4>💱 Currency Movements</h4>
              <p>Exchange rate fluctuations affecting multinational corporations</p>
            </div>
            <div className="factor">
              <h4>🛢️ Commodity Prices</h4>
              <p>Oil and other commodity price changes impacting various sectors</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-tips" variants={itemVariants}>
          <h2>Index Trading Tips</h2>
          <ul className="tips-list">
            <li>Monitor major economic calendars for scheduled data releases</li>
            <li>Pay attention to opening hours of different markets for optimal liquidity</li>
            <li>Understand the composition and weighting methodology of each index</li>
            <li>Use proper position sizing and risk management techniques</li>
            <li>Consider correlations between different indices when building a portfolio</li>
            <li>Stay informed about sector-specific news affecting index components</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Trading Global Indices</h2>
          <p>Access professional signals and analysis for major stock indices worldwide</p>
          <div className="btn-container">
            <Link to="/services/signals" className="btn">Get Trading Signals</Link>
            <Link to="/education" className="btn secondary">Learn More</Link>
            <Link to="/ib-partners" className="btn secondary">Choose Your Broker</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Indices;
